/**
 * API Client - Axios wrapper with authentication and error handling
 *
 * Features:
 * - Automatic JWT token injection
 * - Token refresh on 401
 * - Request/response interceptors
 * - Error normalization
 */
import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';

// =============================================================================
// Types
// =============================================================================

interface AuthTokens {
  accessToken: string;
  refreshToken?: string;
}

interface ApiError {
  message: string;
  code?: string;
  status: number;
  details?: Record<string, unknown>;
}

// =============================================================================
// Token Storage
// =============================================================================

const TOKEN_KEY = 'kakuat_auth_token';
const REFRESH_KEY = 'kakuat_refresh_token';

export function getStoredTokens(): AuthTokens | null {
  const accessToken = localStorage.getItem(TOKEN_KEY);
  const refreshToken = localStorage.getItem(REFRESH_KEY);

  if (!accessToken) return null;

  return { accessToken, refreshToken: refreshToken || undefined };
}

export function setStoredTokens(tokens: AuthTokens): void {
  localStorage.setItem(TOKEN_KEY, tokens.accessToken);
  if (tokens.refreshToken) {
    localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
  }
}

export function clearStoredTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

/**
 * El fin de sesión REAL del empleado pasa por el 401 de este interceptor
 * (caducidad del refresh → redirect a /login), no por `logout()`: la PWA
 * del trabajador sólo recientemente tiene botón de salir, y el camino
 * habitual sigue siendo la caducidad. Misma salida de sesión, misma
 * limpieza: sin esto, la caché de centros de localStorage (nombres,
 * coordenadas, radios — datos de la empresa de quien se va) sobrevivía al
 * cambio de usuario y el siguiente veía «Dentro de <centro de la empresa
 * de A>» (ronda 3, r2b18[6]). Es la misma limpieza que ya hace logout().
 */
function cerrarSesionLocal(): void {
  clearStoredTokens();
  try {
    localStorage.removeItem('kakuat-worksites');
  } catch {
    /* almacenamiento no disponible: nada que limpiar */
  }
}

// =============================================================================
// API Client Instance
// =============================================================================

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - Add auth token
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const tokens = getStoredTokens();
      if (tokens?.accessToken) {
        config.headers.Authorization = `Bearer ${tokens.accessToken}`;
      }

      // Add device fingerprint if available
      const fingerprint = localStorage.getItem('kakuat_device_fingerprint');
      if (fingerprint) {
        config.headers['X-Device-Fingerprint'] = fingerprint;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - Handle errors
  /**
 * Un solo refresh a la vez, aunque caduquen diez peticiones juntas.
 *
 * El interceptor lanzaba SU PROPIO `POST /auth/refresh` por cada 401. El
 * backend ROTA el refresh token, así que el segundo llegaba con uno ya
 * revocado, fallaba, y su `catch` borraba los tokens y mandaba a `/login`: al
 * trabajador se le cerraba la sesión en mitad del turno porque el sync de los
 * 30 s y una navegación suya coincidieron. El guard `_retried` evita el bucle
 * por petición, no la carrera entre peticiones.
 */
let refrescoEnCurso: Promise<AuthTokens> | null = null;

async function refrescarUnaSolaVez(refreshToken: string): Promise<AuthTokens> {
  if (!refrescoEnCurso) {
    refrescoEnCurso = axios
      .post(`${API_BASE_URL}/auth/refresh`, { refresh_token: refreshToken })
      .then((res) => {
        const tokens: AuthTokens = {
          accessToken: res.data.access_token,
          refreshToken: res.data.refresh_token,
        };
        setStoredTokens(tokens);
        return tokens;
      })
      .finally(() => {
        refrescoEnCurso = null;
      });
  }
  return refrescoEnCurso;
}

client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as (typeof error.config & { _retried?: boolean }) | undefined;

      // Handle 401 Unauthorized
      if (error.response?.status === 401 && originalRequest && !originalRequest._retried) {
        // Un solo intento de refresh por petición: si la petición reintentada
        // vuelve a dar 401, no se entra en bucle refresh→retry→401→refresh.
        originalRequest._retried = true;
        // Token expired - try to refresh
        const tokens = getStoredTokens();
        if (tokens?.refreshToken) {
          try {
            const newTokens = await refrescarUnaSolaVez(tokens.refreshToken);

            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
            return client(originalRequest);
          } catch (refreshError) {
            // Refresh failed - clear tokens and redirect to login
            cerrarSesionLocal();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        } else {
          // No refresh token - redirect to login
          cerrarSesionLocal();
          window.location.href = '/login';
        }
      }

      // Normalize error
      const apiError: ApiError = {
        message: 'Error de conexión',
        status: error.response?.status || 0,
      };

      // Con `responseType: 'blob'` (los endpoints que devuelven PNG o un
      // fichero) axios entrega también los CUERPOS DE ERROR como Blob. Sin
      // desenvolverlo aquí, `data.detail` era `undefined` y el mensaje se
      // quedaba en "Error de conexión": el responsable veía un problema de
      // red inexistente justo en la pantalla donde tenía que entender por
      // qué no puede reemitir un QR.
      let cuerpo = error.response?.data;
      if (cuerpo instanceof Blob) {
        try {
          cuerpo = JSON.parse(await cuerpo.text());
        } catch {
          cuerpo = undefined;
        }
      }

      if (cuerpo) {
        const data = cuerpo as Record<string, unknown>;
        if (typeof data.detail === 'string') {
          apiError.message = data.detail;
        } else if (Array.isArray(data.detail)) {
          // 422 de validación de Pydantic (detail = array de {loc, msg}):
          // antes caía al "object" de abajo y salía "Error del servidor" —
          // el guardado entero moría sin decir QUÉ campo lo mató (r2b20[9]).
          apiError.message = detailValidacionLegible(data.detail) || 'Error del servidor';
        } else if (typeof data.detail === 'object' && data.detail) {
          const detail = data.detail as Record<string, unknown>;
          apiError.message = (detail.message as string) || 'Error del servidor';
          apiError.code = detail.code as string;
          apiError.details = detail;
        } else if (data.message) {
          apiError.message = data.message as string;
        }
      } else if (error.message === 'Network Error') {
        apiError.message = 'Sin conexión a internet';
      } else if (error.code === 'ECONNABORTED') {
        apiError.message = 'Tiempo de espera agotado';
      }

      return Promise.reject({ ...error, apiError });
    }
  );

  return client;
};

// Singleton instance
export const apiClient = createApiClient();

// =============================================================================
// Error helpers
// =============================================================================

// Etiquetas humanas de los campos que este frontend escribe: el 422 de
// validación de Pydantic llega como `detail: [{loc, msg, ...}]` y sin este
// mapa el mensaje aplana a "Error del servidor" — el admin veía que el
// guardado entero moría "por el servidor" cuando era SU campo (r2b20[9]).
// Los campos que faltan caen al nombre crudo: mejor el nombre técnico que
// culpar al servidor.
const FIELD_LABELS: Record<string, string> = {
  name: 'El nombre',
  night_start: 'El inicio de la franja nocturna',
  night_end: 'El fin de la franja nocturna',
  max_overtime_annual: 'Las horas extra anuales',
  annual_hours: 'La jornada anual',
  vacation_days_annual: 'Los días de vacaciones',
  lunch_deduction_minutes: 'Los minutos de comida',
  lunch_auto_deduct: 'La comida automática',
  standard_daily_hours: 'La jornada diaria',
  duration_days: 'Los días del paso',
  shift_template_id: 'El turno del paso',
  date: 'La fecha',
  hire_date: 'La fecha de alta',
  weekly_hours: 'Las horas semanales',
  employee_code: 'El código de empleado',
  email: 'El correo',
  password: 'La contraseña',
  pin: 'El PIN',
};

function traducirMsgPydantic(msg: string): string {
  // Los mensajes que más se ven por esta UI, en castellano; el resto viaja
  // tal cual (mejor inglés técnico que un "Error del servidor" que miente).
  if (msg.includes('should match pattern')) {
    // El patrón HH:MM de las horas de la franja nocturna
    // ("^([01]\d|2[0-3]):[0-5]\d$") es el que llega cuando se vacía un
    // input type="time": se reconoce por su clase de dígitos.
    return msg.includes('[01]') && msg.includes(':')
      ? 'la hora no tiene el formato HH:MM'
      : 'el valor no tiene el formato esperado';
  }
  if (msg.includes('greater than')) return 'tiene que ser mayor que el mínimo';
  if (msg.includes('less than')) return 'supera el máximo permitido';
  if (msg.includes('Field required')) return 'falta este dato';
  if (msg.includes('valid integer')) return 'no es un número válido';
  if (msg.includes('valid date')) return 'no es una fecha válida';
  return msg;
}

/** `detail` array de un 422 de Pydantic → "Campo: mensaje" legibles, separados por «;». */
function detailValidacionLegible(detalle: unknown[]): string {
  return detalle
    .filter((e): e is Record<string, unknown> => typeof e === 'object' && e !== null)
    .map((e) => {
      const loc = Array.isArray(e.loc) ? (e.loc as unknown[]) : [];
      const crudo = [...loc].reverse().find((p) => typeof p === 'string' && p !== 'body');
      const campo = typeof crudo === 'string' ? crudo : undefined;
      const etiqueta = campo != null ? FIELD_LABELS[campo] ?? campo : 'un campo';
      const msg = typeof e.msg === 'string' ? traducirMsgPydantic(e.msg) : 'no es válido';
      return `${etiqueta}: ${msg}`;
    })
    .join('; ');
}

/** Extrae el mensaje de `apiError` (ver interceptor arriba) o cae al fallback. */
export function apiMessage(e: unknown, fallback: string): string {
  const apiError = (e as { apiError?: { status?: number; message?: string } })?.apiError;
  return apiError?.message ?? fallback;
}
